import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import {
  IconPhone, IconBrandWhatsapp, IconMail, IconWorld,
  IconBrandLinkedin, IconMapPin, IconBriefcase, IconSchool,
  IconCertificate, IconLanguage, IconX, IconDeviceFloppy, IconEye, IconEyeOff,
} from '@tabler/icons-react'
import { LawyerProfilePayload } from '@/api/lawyer-profile'

interface LawyerProfilePreviewProps {
  form: Partial<LawyerProfilePayload>
  photoUrl?: string | null
  onClose: () => void
  onSave: () => void
  saving: boolean
}

// ── Format a career period ─────────────────────────────────────────────────
function formatPeriod(
  year: number | null | undefined,
  month: number | null | undefined,
  fallback: string
): string {
  if (!year) return fallback
  if (month) return `${String(month).padStart(2, '0')}/${year}`
  return String(year)
}

export function LawyerProfilePreview({
  form, photoUrl, onClose, onSave, saving,
}: LawyerProfilePreviewProps) {
  const { t, i18n } = useTranslation()
  const isAr = i18n.language === 'ar'

  const initials = (form.full_name || '')
    .split(' ')
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() || '')
    .join('')

  return (
    <div className="fixed inset-0 z-50 bg-background/80 backdrop-blur-sm flex items-start justify-center overflow-y-auto py-8 px-4">
      <div className="w-full max-w-2xl space-y-4">

        {/* Preview toolbar */}
        <div className="flex items-center justify-between bg-background border border-border rounded-xl px-4 py-3 sticky top-0 z-10 shadow-sm">
          <div className="flex items-center gap-2">
            <IconEye size={16} className="text-primary" />
            <span className="text-sm font-medium">{t('preview_mode')}</span>
            {form.is_published ? (
              <Badge variant="default" className="text-xs">{t('published')}</Badge>
            ) : (
              <Badge variant="outline" className="text-xs gap-1">
                <IconEyeOff size={10} />
                {t('draft')}
              </Badge>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={onClose} className="gap-1">
              <IconX size={14} />{t('close_preview')}
            </Button>
            <Button size="sm" onClick={onSave} disabled={saving} className="gap-1">
              <IconDeviceFloppy size={14} />
              {saving ? t('saving') : t('save')}
            </Button>
          </div>
        </div>

        {/* Profile card */}
        <div className="bg-background border border-border rounded-xl overflow-hidden">

          {/* Header banner */}
          <div className="h-24 bg-gradient-to-r from-primary/10 to-primary/5 border-b border-border" />

          {/* Avatar + name */}
          <div className="px-6 pb-6">
            <div className="flex items-end gap-4 -mt-10 mb-4">
              <div className="w-20 h-20 rounded-full border-4 border-background bg-muted overflow-hidden flex items-center justify-center flex-shrink-0 shadow-sm">
                {photoUrl ? (
                  <img src={photoUrl} alt={form.full_name} className="w-full h-full object-cover" />
                ) : (
                  <span className="text-xl font-medium text-muted-foreground">{initials || '?'}</span>
                )}
              </div>
              <div className="pb-1">
                <h1 className="text-xl font-semibold leading-tight">
                  {form.full_name || <span className="text-muted-foreground italic">{t('no_name')}</span>}
                </h1>
                <p className="text-sm text-muted-foreground">
                  {form.title || 'Avocat'}
                  {form.bar_city && ` — ${t('barreau_de')} ${form.bar_city}`}
                </p>
                <p className="text-sm text-muted-foreground">
                  {form.title || 'Avocat'}
                  {form.full_name_fr || <span className="text-muted-foreground italic">{t('no_name')}</span>}
                  {form.rattachement_year && ` (${t('since')} ${form.rattachement_year})`}
                </p>
              </div>
            </div>

            {/* Bio */}
            {form.show_bio && (form.bio_ar || form.bio_fr) && (
              <div className="mb-5 p-4 bg-muted/40 rounded-lg border border-border/50">
                {isAr && form.bio_ar && <p className="text-sm leading-relaxed" dir="rtl">{form.bio_ar}</p>}
                {!isAr && form.bio_fr && <p className="text-sm leading-relaxed">{form.bio_fr}</p>}
                {isAr && !form.bio_ar && form.bio_fr && <p className="text-sm leading-relaxed">{form.bio_fr}</p>}
                {!isAr && !form.bio_fr && form.bio_ar && <p className="text-sm leading-relaxed" dir="rtl">{form.bio_ar}</p>}
              </div>
            )}

            {/* Specializations */}
            {form.show_specializations && (form.specializations || []).length > 0 && (
              <div className="mb-5">
                <h3 className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2">{t('specializations')}</h3>
                <div className="flex flex-wrap gap-2">
                  {(form.specializations || []).map((spec) => (
                    <span key={spec.key} className="px-3 py-1 text-xs rounded-full bg-primary/10 text-primary border border-primary/20 font-medium">
                      {isAr ? spec.ar : spec.fr}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Contact */}
            {form.show_contact && (
              <div className="mb-5">
                <h3 className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2">{t('contact')}</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {form.phone && (
                    <div className="flex items-center gap-2 text-sm">
                      <IconPhone size={14} className="text-muted-foreground flex-shrink-0" />
                      <span>{form.phone}</span>
                    </div>
                  )}
                  {form.whatsapp && (
                    <div className="flex items-center gap-2 text-sm">
                      <IconBrandWhatsapp size={14} className="text-green-600 flex-shrink-0" />
                      <span>{form.whatsapp}</span>
                    </div>
                  )}
                  {form.email_public && (
                    <div className="flex items-center gap-2 text-sm">
                      <IconMail size={14} className="text-muted-foreground flex-shrink-0" />
                      <span className="text-primary">{form.email_public}</span>
                    </div>
                  )}
                  {form.website && (
                    <div className="flex items-center gap-2 text-sm">
                      <IconWorld size={14} className="text-muted-foreground flex-shrink-0" />
                      <span className="text-primary">{form.website}</span>
                    </div>
                  )}
                  {form.linkedin && (
                    <div className="flex items-center gap-2 text-sm sm:col-span-2">
                      <IconBrandLinkedin size={14} className="text-blue-600 flex-shrink-0" />
                      <span className="text-primary truncate">{form.linkedin}</span>
                    </div>
                  )}
                  {form.google_maps_url && (
                    <div className="flex items-center gap-2 text-sm sm:col-span-2">
                      <IconMapPin size={14} className="text-primary flex-shrink-0" />
                      <a href={form.google_maps_url} target="_blank" rel="noopener noreferrer"
                        className="text-primary hover:underline">{t('view_on_maps')}</a>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Cabinet location */}
            {(form.cabinet_address || form.cabinet_city) && (
              <div className="mb-5">
                <h3 className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2">{t('cabinet_location')}</h3>
                <div className="flex items-start gap-2 text-sm">
                  <IconMapPin size={14} className="text-muted-foreground flex-shrink-0 mt-0.5" />
                  <span>{[form.cabinet_address, form.cabinet_city, form.cabinet_country].filter(Boolean).join(', ')}</span>
                </div>
              </div>
            )}

            {/* Experience */}
            {form.show_experience && form.rattachement_year && (
              <div className="mb-5">
                <h3 className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2">
                  {t('experience')}
                </h3>
                <div className="flex items-center gap-2 text-sm">
                  <IconBriefcase size={14} className="text-muted-foreground" />
                  <span>
                    {new Date().getFullYear() - form.rattachement_year} {t('years')}
                  </span>
                </div>
              </div>
            )}

            {/* Career */}
            {form.show_career && (form.career || []).length > 0 && (
              <div className="mb-5">
                <h3 className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2">{t('career')}</h3>
                <div className="space-y-2">
                  {(form.career || []).map((entry, i) => {
                    const fromStr = formatPeriod(entry.from, entry.from_month, '?')
                    const toStr = entry.current
                      ? t('present')
                      : formatPeriod(entry.to, entry.to_month, '?')
                    return (
                      <div key={i} className="flex items-start gap-3 p-3 rounded-lg bg-muted/40 border border-border/50">
                        <IconBriefcase size={16} className="text-primary flex-shrink-0 mt-0.5" />
                        <div>
                          <p className="text-sm font-medium">{entry.role}</p>
                          <p className="text-xs text-muted-foreground">
                            {entry.cabinet}{entry.city && ` — ${entry.city}`}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {fromStr} — {toStr}
                          </p>
                        </div>
                      </div>
                    )
                  })}
                </div>
              </div>
            )}

            {/* Education */}
            {form.show_education && (form.education || []).length > 0 && (
              <div className="mb-5">
                <h3 className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2">{t('education')}</h3>
                <div className="space-y-2">
                  {(form.education || []).map((edu, i) => (
                    <div key={i} className="flex items-start gap-3 p-3 rounded-lg bg-muted/40 border border-border/50">
                      <IconSchool size={16} className="text-primary flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-sm font-medium">{edu.degree}</p>
                        <p className="text-xs text-muted-foreground">{edu.institution} — {edu.year}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Certifications */}
            {form.show_certifications && (form.certifications || []).length > 0 && (
              <div className="mb-5">
                <h3 className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2">{t('certifications')}</h3>
                <div className="space-y-2">
                  {(form.certifications || []).map((cert, i) => (
                    <div key={i} className="flex items-start gap-3 p-3 rounded-lg bg-muted/40 border border-border/50">
                      <IconCertificate size={16} className="text-primary flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-sm font-medium">{cert.title}</p>
                        <p className="text-xs text-muted-foreground">{cert.issuer} — {cert.year}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Languages */}
            {form.show_languages && (form.languages_spoken || []).length > 0 && (
              <div>
                <h3 className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2">{t('languages_spoken')}</h3>
                <div className="flex items-center gap-2">
                  <IconLanguage size={14} className="text-muted-foreground" />
                  <div className="flex flex-wrap gap-2">
                    {(form.languages_spoken || []).map((lang) => (
                      <span key={lang} className="px-2.5 py-0.5 text-xs rounded-full border border-border bg-muted/50 font-medium">
                        {lang.toUpperCase()}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Bottom save */}
        <div className="flex justify-end gap-2 pb-4">
          <Button variant="outline" onClick={onClose}>{t('back_to_edit')}</Button>
          <Button onClick={onSave} disabled={saving} className="gap-1">
            <IconDeviceFloppy size={14} />
            {saving ? t('saving') : t('save_profile')}
          </Button>
        </div>
      </div>
    </div>
  )
}